/**
 * PhotoCapture — capture/upload control for evidence photos.
 * Two trigger buttons: Capture (camera capture="environment", mobile) and
 * Upload (file picker, multiple). Uploads to arbi-photos bucket under
 * `${entity_type}/${entity_id}/${uuid}.${ext}`, then inserts a `photos` row
 * tying the storage object to its entity.
 *
 * RLS: insert WITH CHECK (auth.uid() = uploaded_by) — caller must be signed in.
 * readOnly returns null so a parent doesn't have to conditional-render.
 */

import { useState, useRef, type JSX } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';

interface PhotoCaptureProps {
  entityType: 'receiving_line_item' | 'discrepancy_resolution';
  entityId: string;
  onUploaded?: () => void;
  readOnly?: boolean;
}

export default function PhotoCapture({
  entityType,
  entityId,
  onUploaded,
  readOnly = false,
}: PhotoCaptureProps): JSX.Element | null {
  const { user } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (readOnly) return null;

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0 || !user) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop() || 'jpg';
        const path = `${entityType}/${entityId}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('arbi-photos').upload(path, file, {
          contentType: file.type,
          upsert: false,
        });
        if (upErr) throw upErr;
        const { error: insErr } = await supabase.from('photos').insert({
          entity_type: entityType,
          entity_id: entityId,
          storage_path: path,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id,
        } as never);
        if (insErr) throw insErr;
      }
      onUploaded?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        aria-hidden="true"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={() => cameraInputRef.current?.click()}
        disabled={uploading}
        className="arbi-btn-ghost text-xs flex items-center gap-1"
      >
        <Camera size={14} /> Capture
      </button>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="arbi-btn-ghost text-xs flex items-center gap-1"
      >
        <Upload size={14} /> Upload
      </button>
      {uploading && <span className="text-xs text-ink-muted">Uploading…</span>}
      {error && (
        <span className="text-xs text-danger flex items-center gap-1">
          <X size={12} /> {error}
        </span>
      )}
    </div>
  );
}
